import React, { useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import {
  Alert,
  Box,
  Divider,
  Tooltip,
  Typography
} from '@mui/material'

import DetailsSummarySection from '../../components/details/summary/DetailsSummarySection'
import DownloadActionButton from '../../components/action_button/DownloadActionButton'
import LoadingScreen from '../../components/LoadingScreen'
import TabsMui from '../../layout/TabsMui'
import {
  formatShortDateRange,
  valueOrDash
} from '../../helpers/utils'
import {
  buildFillRateProductSummary,
  resolveFillRateCategoryLabel
} from '../PurchaseOrder/helpers/fillRateMetrics'
import {
  FILL_RATE_PRODUCT_DIFFERENCES_DETAIL_TAB,
  FILL_RATE_PRODUCT_DIFFERENCES_TABS
} from './constants/fillRateProductDifferencesConstants'
import FillRateProductDifferenceTotalValue from './components/FillRateProductDifferenceTotalValue'
import FillRateProductDifferencesTable from './components/FillRateProductDifferencesTable'
import { exportFillRateProductDifferencesCsv } from './helpers/downloads/exportFillRateProductDifferencesCsv'
import { useFillRateProductDifferences } from './hooks/useFillRateProductDifferences'
import {
  FILL_RATE_PRODUCT_DETAILS_DIVIDER_SX,
  FILL_RATE_PRODUCT_DETAILS_HEADER_SX,
  FILL_RATE_PRODUCT_DETAILS_SUMMARY_SX,
  FILL_RATE_PRODUCT_DETAILS_TITLE_SX
} from './style/fillRateProductDifferencesStyles'

const FillRateProductDifferences = () => {
  const location = useLocation()
  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  )

  const startDate = searchParams.get('start_date')
  const endDate = searchParams.get('end_date')
  const buyer = searchParams.get('buyer')
  const productGroup = searchParams.get('product_group')
  const productGroupLabel = resolveFillRateCategoryLabel(productGroup)
  const missingParams = !startDate || !endDate || !buyer || !productGroup
  const [loadingDownload, setLoadingDownload] = useState(false)

  const { loading, data, error } = useFillRateProductDifferences({
    startDate,
    endDate,
    buyer,
    productGroup
  })

  const productSummary = useMemo(
    () => buildFillRateProductSummary(data?.results || []),
    [data?.results]
  )
  const rows = productSummary.rows
  const hasRows = rows.length > 0
  const dateRangeLabel = formatShortDateRange(startDate, endDate)
  const hasResponseErrors = data?.errors?.length > 0
  const showDetails = !missingParams && !loading && !error && !hasResponseErrors

  const handleDownload = async () => {
    setLoadingDownload(true)

    try {
      exportFillRateProductDifferencesCsv({
        buyer,
        productGroupLabel,
        dateRangeLabel,
        productsWithDifferenceCount: productSummary.productsWithDifferenceCount,
        differenceTotal: productSummary.differenceTotal,
        rows
      })
    } finally {
      setLoadingDownload(false)
    }
  }
  const summaryFields = useMemo(
    () => [
      { label: 'GRUPO DE PRODUCTOS', value: productGroupLabel },
      { label: 'RANGO DE FECHA', value: dateRangeLabel },
      {
        label: 'PRODUCTOS CON DIFERENCIA',
        value: String(productSummary.productsWithDifferenceCount)
      },
      {
        label: 'DIFERENCIA TOTAL',
        value: <FillRateProductDifferenceTotalValue value={productSummary.differenceTotal} />
      }
    ],
    [
      dateRangeLabel,
      productGroupLabel,
      productSummary.differenceTotal,
      productSummary.productsWithDifferenceCount
    ]
  )

  return (
    <>
      <div className="main-show-header">
        <h2>Productos con diferencias: {valueOrDash(buyer)}</h2>
      </div>

      <div className="index-table">
        <div className="show-area">
          {missingParams && (
            <Alert severity="warning">
              Faltan filtros para consultar productos con diferencias.
            </Alert>
          )}

          {!missingParams && loading && (
            <LoadingScreen text="Cargando..." />
          )}

          {!missingParams && !loading && error && (
            <Alert severity="error">
              Error cargando productos con diferencias.
            </Alert>
          )}

          {!missingParams && !loading && !error && hasResponseErrors && (
            <Alert severity="error">
              {data.errors.join(' | ')}
            </Alert>
          )}

          {showDetails && (
            <>
              <div className="show-area-tabs-header">
                <TabsMui
                  tabs={FILL_RATE_PRODUCT_DIFFERENCES_TABS}
                  activeTab={FILL_RATE_PRODUCT_DIFFERENCES_DETAIL_TAB}
                  onChange={() => FILL_RATE_PRODUCT_DIFFERENCES_DETAIL_TAB}
                  showDivider={false}
                  marginBottom={0}
                />
              </div>

              <div className="tab-content-area">
                <Box sx={FILL_RATE_PRODUCT_DETAILS_HEADER_SX}>
                  <Typography className="text-sub-title" sx={FILL_RATE_PRODUCT_DETAILS_TITLE_SX}>
                    Detalles de productos:
                  </Typography>

                  <Tooltip title={hasRows ? 'Descargar CSV' : 'No hay resultados para descargar'}>
                    <span>
                      <DownloadActionButton
                        onClick={handleDownload}
                        disabled={loadingDownload || !hasRows}
                        density="toolbar"
                        fullWidth={false}
                        text={loadingDownload ? 'Descargando...' : 'Descargar'}
                      />
                    </span>
                  </Tooltip>
                </Box>

                <Divider sx={FILL_RATE_PRODUCT_DETAILS_DIVIDER_SX} />

                <Box sx={FILL_RATE_PRODUCT_DETAILS_SUMMARY_SX}>
                  <DetailsSummarySection
                    summaryFields={summaryFields}
                    summaryColumns={{
                      xs: '1fr',
                      sm: '1fr 1fr',
                      lg: 'repeat(4, minmax(0, 1fr))'
                    }}
                    summaryTitle="RESUMEN"
                    summaryOpenLabel="Abrir resumen"
                    summaryCloseLabel="Cerrar resumen"
                  />
                </Box>

                <FillRateProductDifferencesTable rows={rows} />
              </div>
            </>
          )}
        </div>
      </div>
    </>
  )
}

export default FillRateProductDifferences
